import React, { useCallback, useState } from "react";
import { Grid } from "@mui/material";
import { useFormikContext } from "formik";

import ExtendCreativeChannelsBase from "./ExtendCreativeChannels";
import ExtendCreativeOptionsBase from "./ExtendCreativeOptions";
import { RpSweetAlert } from "@/components/alerts/RpSweetAlert";

type LocationId = number | string;

type CreativeOption = {
  id: LocationId;
  [key: string]: unknown;
};

type SelectedLocationValue = {
  extend_creatives: LocationId[];
};

type CreativeFormikValues = {
  selectedLoc: Record<LocationId, SelectedLocationValue>;
};

type CreativeViewProps = {
  location_id: LocationId;
  creatives?: CreativeOption[];
  location_name?: string;
  can_update_creatives?: boolean;
  creative_select_max_value?: number | string | null;
};

const ExtendCreativeOptions =
  ExtendCreativeOptionsBase as unknown as React.ComponentType<
    Record<string, unknown>
  >;

const ExtendCreativeChannels =
  ExtendCreativeChannelsBase as unknown as React.ComponentType<
    Record<string, unknown>
  >;

function CreativeView({
  location_id,
  creatives,
  location_name,
  can_update_creatives,
  creative_select_max_value,
}: CreativeViewProps) {
  const { setFieldValue, values } = useFormikContext<CreativeFormikValues>();

  const [viewCreative, setViewCreative] = useState(() => {
    return creatives && creatives.length ? creatives[0].id : 0;
  });

  const [previewModalOpen, setPreviewModalOpen] = useState(false);

  const handleSelectCreative = useCallback(
    (id: LocationId) => {
      setViewCreative(id);
      const selectedCreatives =
        values.selectedLoc[location_id].extend_creatives || [];

      if (selectedCreatives.includes(id)) {
        setFieldValue(
          `selectedLoc.${location_id}.extend_creatives`,
          selectedCreatives.filter((i) => i !== id),
          true
        );
      } else {
        // If max value is null, treat it as 1.
        const maxValue =
          creative_select_max_value === null
            ? 1
            : Number(creative_select_max_value);

        if (maxValue && selectedCreatives.length >= maxValue) {
          RpSweetAlert.fire({
            icon: "info",
            html: `<p>You can only select <b>up to ${maxValue} creatives</b></p>`,
            showConfirmButton: true,
            confirmButtonText: "OK",
            confirmButtonColor: "var(--primary-base)",
            showCancelButton: false,
          });
          return;
        }
        setFieldValue(
          `selectedLoc.${location_id}.extend_creatives`,
          [...selectedCreatives, id],
          true
        );
      }
    },
    [location_id, setFieldValue, values, creative_select_max_value]
  );

  const handlePreview = useCallback(
    (id: LocationId) => {
      setViewCreative(id);
      setPreviewModalOpen(true);
    },
    [setViewCreative]
  );

  const handleClosePreviewModal = useCallback(() => {
    setPreviewModalOpen(false);
  }, []);

  return (
    <div>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 5, lg: 3 }}>
          <ExtendCreativeOptions
            creatives={creatives}
            viewCreative={viewCreative}
            handleSelectCreative={handleSelectCreative}
            onPreview={handlePreview}
            location_id={location_id}
            can_update_creatives={can_update_creatives}
            creative_select_max_value={creative_select_max_value}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 7, lg: 9 }}>
          <ExtendCreativeChannels
            creatives={creatives}
            viewCreative={viewCreative}
            location_id={location_id}
            previewModalOpen={previewModalOpen}
            onClosePreviewModal={handleClosePreviewModal}
            location_name={location_name}
          />
        </Grid>
      </Grid>
    </div>
  );
}

export default CreativeView;
